import React, { useState, useCallback, useEffect } from 'react';
import { range } from 'lodash';
import PropTypes from 'prop-types';
import './AudienceTargetModal.scss';
import ModalWithConfirmationButtons from '../../../../../common/Modal/ModalWithConfirmationButtons/ModalWithConfirmationButtons';
import Dropdown from '../../../../../common/Dropdown/Dropdown/Dropdown';
import { isEmpty, startCase } from 'lodash';
import Checkbox from '../../../../../common/Checkbox/Checkbox';
import ClickableIcon from '../../../../../common/ClickableIcon/ClickableIcon';
import Slider from '../../../../../common/Slider/Slider';
import EstimatedReachWidget from '../../EstimatedReachWidget/EstimatedReachWidget';
import Separator from '../../../../../common/Separator/Separator';
import TabSelector from '../../../../../common/TabSelector/TabSelector';
import TextBox from '../../../../../common/TextBox/TextBox';
import * as MixpanelCommon from '../../../../../../infra/mixpanel/MixpanelCommon';
import { getChannelName, getAbiPermissionsService } from '../../../../../../middleware/AngularExportedMiddleware';
import {
  getGeoFromAudienceSegment,
  getSummaryTextBuilder,
  SYNDICATION_VALUES,
} from '../../../../../../../data/audience-segment-builder-helper';
import {
  MIN_REACH_GOAL,
  NUM_USERS_BY_GEO,
  MAX_REACH_GOAL,
  REACH_GOAL_STEPS,
  CUSTOM_PRICE_STEPS,
  MIN_CUSTOM_PRICE,
  MAX_CUSTOM_PRICE,
} from '../../../../../../services/AudienceInsightsService';
import { Radio, RadioGroup, Switch } from '@amobee/component-library';
import { DatepickerButton } from '@amobee/component-library';

const PERMISSION_CUSTOM_AMPLIFY_PERCENTAGE = 'custom amplify percentage';
const REQUIRED_ERROR_MESSAGE = 'Required field';
const CUSTOM_PRICE = 'Custom Price';
const ADD_CUSTOM_PRICE = 'Add Custom Price';
const customFieldHeight = '32px';
const amplificationModes = [
  { label: 'Custom', value: 'custom' },
  { label: 'Always On', value: 'always_on' },
];
const optimizationOptions = [
  { label: 'Optimize Reach', value: 'REACH' },
  { label: 'Clicks', value: 'CLICKS' },
  { label: 'Actions', value: 'ACTIONS' },
];

const AudienceTargetModal = ({
  isOpen,
  onSubmitCustom,
  onSubmitAlwaysOn,
  onCancel,
  marketsAndAdvertisersPromise,
  audienceName,
  isProcessing,
  getSegmentIds,
  dspService,
  audienceSegment,
  channel,
  isAudienceDeterministicActivated,
  isAudienceAmplifiedActivated,
  isAudienceAlwaysOnActivated,
  activatedAmplifiedThreshold,
  activatedAlwaysOnThreshold,
  activatedMarket,
  activatedAdvertiser,
  activatedDataContractId,
  activatedDataContractText,
  activatedCategoryId,
  getAmplifiedEstimatedReachGoal,
  widgetName,
  hasAmplificationModeSelector,
  getDataContract,
  getTaxonomyCategory,
  isDeterministicDynamicAllowed,
  allowDeterministicSegment,
  allowCustomPriceSegment,
  allowOptimizeSegment,
}) => {
  const defaultExpirationDate = isDeterministicDynamicAllowed
    ? null
    : new Date(new Date().setMonth(new Date().getMonth() + 6));
  const geo = getGeoFromAudienceSegment(audienceSegment);
  const isDisableAmplify = ['DE'].includes(geo); // check if i can import cc of Germany in dynamic way
  const bidstreamUsers = NUM_USERS_BY_GEO[geo] || NUM_USERS_BY_GEO.US;
  const modalTitle = 'Activate your audience';
  const amplifiedHelpDescription =
    'Audience size ranges from 1% to 20% of the targetable audience. A 1% amplification consists of the people most similar to your audience source. Increasing the percentage creates a bigger, broader audience.';
  const allAdvertisersOption = { value: -1, label: 'All advertisers', name: 'All advertisers' };
  const defaultReachGoal = { value: 2, label: 2 };
  const maxReachGoal = { value: MAX_REACH_GOAL, label: MAX_REACH_GOAL };
  const isCustomAudienceActivated = isAudienceDeterministicActivated || isAudienceAmplifiedActivated;
  const [marketsPromise] = useState(
    activatedMarket ? Promise.resolve([activatedMarket]) : marketsAndAdvertisersPromise.then((res) => res.markets)
  );
  const [selectedMarket, setSelectedMarket] = useState(activatedMarket || {});
  const [selectedAdvertiser, setSelectedAdvertiser] = useState(activatedAdvertiser || {});
  const [isSegmentOptimized, setIsSegmentOptimized] = useState(false);
  const [selectedOptimization, setSelectedOptimization] = useState(optimizationOptions[0].value);
  const [isAmplified, setIsAmplified] = useState(channel === 'articles');
  const [tremorSyndicateAmplified, setTremorSyndicateAmplified] = useState([]);
  const [tremorSyndicateDeterministic, setTremorSyndicateDeterministic] = useState([]);
  const [isCustomPriceAmplified, setIsCustomPriceAmplified] = useState(false);
  const [isCustomPriceDeterministic, setIsCustomPriceDeterministic] = useState(false);
  const [customPriceAmplified, setCustomPriceAmplified] = useState('');
  const [customPriceDeterministic, setCustomPriceDeterministic] = useState('');
  const [errorMsgCustomPriceAmplified, setErrorMsgCustomPriceAmplified] = useState(null);
  const [errorMsgCustomPriceDeterministic, setErrorMsgCustomPriceDeterministic] = useState(null);
  const [isDeterministic, setIsDeterministic] = useState(false);
  const [expirationDate, setExpirationDate] = useState(defaultExpirationDate);
  const [isDynamic, setIsDynamic] = useState(isDeterministicDynamicAllowed);
  const [amplifiedReachGoal, setAmplifiedReachGoal] = useState(
    activatedAmplifiedThreshold
      ? { value: activatedAmplifiedThreshold, label: activatedAmplifiedThreshold }
      : defaultReachGoal
  );
  const [alwaysOnReachGoal, setAlwaysOnReachGoal] = useState(
    activatedAlwaysOnThreshold
      ? { value: activatedAlwaysOnThreshold, label: activatedAlwaysOnThreshold }
      : defaultReachGoal
  );
  const [isMarketsPromiseLoading, setIsMarketsPromiseLoading] = useState(!isCustomAudienceActivated);
  const [isMarketsDropdownDisabled, setIsMarketsDropdownDisabled] = useState(true);
  const [advertisersPromise, setAdvertisersPromise] = useState(
    activatedAdvertiser
      ? Promise.resolve([activatedAdvertiser])
      : marketsAndAdvertisersPromise.then((res) => [
          ...(res.advertisers.length > 0 ? [allAdvertisersOption] : []),
          ...res.advertisers,
        ])
  );
  const [isEmptyAdvertisers, setIsEmptyAdvertisers] = useState(true);
  const reachGoalValues = range(MIN_REACH_GOAL, MAX_REACH_GOAL + 1).map((x) => ({
    value: x,
    label: x % 2 == 0 ? `${x}%` : '',
  }));
  const [selectedAmplificationModeTab, setselectedAmplificationModeTab] = useState(amplificationModes[0]);
  const [currentAmplificationMode, setAmplificationMode] = useState(amplificationModes[0].value);
  const onSelectAmplificationMode = (selectedAmplificationMode) => {
    setselectedAmplificationModeTab(selectedAmplificationMode);
    const mode = selectedAmplificationMode.value;
    setAmplificationMode(mode);
    if (mode === 'always_on') {
      setIsAmplified(true);
    } else {
      setIsAmplified(channel === 'articles');
      setIsDeterministic(false);
    }
  };
  const [dataContractId, setDataContractId] = useState(activatedDataContractId || '');
  const [dataContractText, setDataContractText] = useState(activatedDataContractText);
  const [dataContractDisp, setDataContractDisp] = useState(
    activatedDataContractText
      ? `${activatedDataContractId} - ${activatedDataContractText}`
      : activatedDataContractId
      ? `${activatedDataContractId}`
      : ''
  );
  const [dataContractIdErrorMessage, setDataContractIdErrorMessage] = useState('');
  const [categoryId, setCategoryId] = useState(activatedCategoryId ? `${activatedCategoryId}` : '');
  const [categoryExists, setCategoryExists] = useState(isAudienceAlwaysOnActivated);
  const [categoryIdErrorMessage, setCategoryIdErrorMessage] = useState('');
  const [lastCheckedDataContractId, setLastCheckedDataContractId] = useState('');
  const [lastReturnedDataContractText, setLastReturnedDataContractText] = useState('');
  const [lastCheckedCategoryId, setLastCheckedCategoryId] = useState('');
  const [lastCategoryExistsResult, setLastCategoryExistsResult] = useState(false);

  const onMarketChange = useCallback(
    (newMarket) => {
      setSelectedMarket(newMarket);
      setAdvertisersPromise(
        dspService.getAmplificationAdvertiserContext(newMarket.value, 'value').then(({ advertisers }) => {
          advertisers.length > 0 ? setSelectedAdvertiser(allAdvertisersOption) : setSelectedAdvertiser(null);
          setIsEmptyAdvertisers(advertisers.length == 0);
          return [...(advertisers.length > 0 ? [allAdvertisersOption] : []), ...advertisers];
        })
      );
    },
    [selectedMarket]
  );

  const onAdvertiserChange = useCallback(
    (newAdvertiser) => {
      setSelectedAdvertiser(newAdvertiser);
    },
    [selectedAdvertiser]
  );

  const onIsAmplifiedClicked = useCallback(
    (newIsAmplified) => {
      setIsAmplified(newIsAmplified);
      setAmplifiedReachGoal(defaultReachGoal);
    },
    [isAmplified, amplifiedReachGoal]
  );

  const onIsDeterministicClicked = useCallback(
    (newIsDeterministic) => {
      setIsDeterministic(newIsDeterministic);
    },
    [isDeterministic]
  );

  const onIsDynamicClicked = useCallback(
    (newIsDynamic) => {
      setIsDynamic(newIsDynamic);
      let newExpirationDate;
      if (!newIsDynamic) {
        newExpirationDate = new Date();
        newExpirationDate.setMonth(newExpirationDate.getMonth() + 6); // default is 6 months
      }
      setExpirationDate(newExpirationDate);
    },
    [isDynamic]
  );

  const modifyValueObject = (value) => {
    if (value.value > MIN_REACH_GOAL) {
      return value;
    } else if (value > MIN_REACH_GOAL) {
      return { value };
    } else {
      return { value: MIN_REACH_GOAL };
    }
  };

  const onAmplifiedReachGoalChanged = useCallback(
    (newAmplifiedReachGoal) => {
      setAmplifiedReachGoal(modifyValueObject(newAmplifiedReachGoal));
    },
    [amplifiedReachGoal]
  );

  const onAlwaysOnReachGoalChanged = useCallback(
    (newAlwaysOnReachGoal) => {
      setAlwaysOnReachGoal(newAlwaysOnReachGoal);
    },
    [alwaysOnReachGoal]
  );

  const minDate = new Date();
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 1); // max expiration date is 1 year
  const convertDateToSeconds = (date) => date.getTime() / 1000;
  const onDaySelected = (dateObj) => {
    //The object emitted from onDaySelected is in seconds, convert it to ms and then to date
    const newExpirationDate = new Date(dateObj.date * 1000);
    setExpirationDate(newExpirationDate);
  };

  function getDataContractDetails() {
    if (!dataContractId) return;
    getDataContract(dataContractId).then((res) => {
      if (res.error) {
        setDataContractText('');
        setLastReturnedDataContractText('');
        setDataContractIdErrorMessage(res.error);
        setDataContractDisp(dataContractId);
      } else {
        setLastReturnedDataContractText(res.name);
        setDataContractText(res.name);
        setDataContractDisp(`${dataContractId} - ${res.name}`);
      }
    });
    getCategoryDetails();
  }

  function getCategoryDetails() {
    if (categoryId) {
      getTaxonomyCategory(dataContractId, categoryId).then((res) => {
        const taxonomyCategoryExists = parseInt(res) !== -1;
        setCategoryExists(taxonomyCategoryExists);
        setLastCategoryExistsResult(taxonomyCategoryExists);
        setCategoryIdErrorMessage(
          taxonomyCategoryExists ? '' : 'Category ID not found. Double check the ID and try again.'
        );
      });
    }
  }

  const onDataContractChange = useCallback(
    ({ target: { value } }) => {
      setDataContractId(value || '');
      setDataContractDisp(value || '');
      setDataContractText('');
    },
    [setDataContractId, setDataContractDisp]
  );

  const onDataContractFocus = useCallback(() => {
    setDataContractDisp(dataContractId);
  }, [setDataContractDisp, dataContractId]);

  const onDataContractBlur = useCallback(() => {
    if (dataContractId === lastCheckedDataContractId) {
      setDataContractText(lastReturnedDataContractText);
      setDataContractDisp(
        lastReturnedDataContractText ? `${dataContractId} - ${lastReturnedDataContractText}` : dataContractId
      );
      return;
    }
    setDataContractIdErrorMessage('');
    setLastCheckedDataContractId(dataContractId);
    getDataContractDetails();
  }, [
    setDataContractDisp,
    dataContractId,
    dataContractText,
    lastCheckedDataContractId,
    lastReturnedDataContractText,
    setLastCheckedDataContractId,
    setDataContractText,
    setDataContractIdErrorMessage,
    categoryId,
    setCategoryExists,
    setCategoryIdErrorMessage,
  ]);

  const onCategoryChange = useCallback(
    ({ target: { value } }) => {
      setCategoryExists(false);
      setCategoryId(value || '');
    },
    [setCategoryId]
  );

  const onCategoryBlur = useCallback(() => {
    if (categoryId === lastCheckedCategoryId) {
      setCategoryExists(lastCategoryExistsResult);
      return;
    }
    setLastCheckedCategoryId(categoryId);
    setCategoryIdErrorMessage('');
    getCategoryDetails();
  }, [
    categoryId,
    lastCheckedCategoryId,
    lastCategoryExistsResult,
    setLastCheckedCategoryId,
    dataContractId,
    setCategoryExists,
    setCategoryIdErrorMessage,
  ]);

  useEffect(() => {
    (async () => {
      if (isCustomAudienceActivated || !marketsAndAdvertisersPromise) return;
      const { markets, advertisers } = await marketsAndAdvertisersPromise;
      setSelectedMarket(markets[0]);
      if (advertisers.length > 0) setSelectedAdvertiser(allAdvertisersOption);
      setIsMarketsDropdownDisabled(markets.length <= 1);
      setIsEmptyAdvertisers(advertisers.length == 0);
      setIsMarketsPromiseLoading(false);
    })();
  }, [marketsAndAdvertisersPromise]);

  useEffect(() => {
    const msg = isCustomPriceAmplified && customPriceAmplified === '' ? REQUIRED_ERROR_MESSAGE : null;
    setErrorMsgCustomPriceAmplified(msg);
  }, [isCustomPriceAmplified, customPriceAmplified]);

  useEffect(() => {
    const msg = isCustomPriceDeterministic && customPriceDeterministic === '' ? REQUIRED_ERROR_MESSAGE : null;
    setErrorMsgCustomPriceDeterministic(msg);
  }, [isCustomPriceDeterministic, customPriceDeterministic]);

  const marketsSummaryTextBuilder = (markets) => (markets.length === 0 ? 'No available markets' : markets[0].label);
  const advertisersSummaryTextBuilder = (advertisers) =>
    advertisers.length === 0 ? 'No available advertisers' : advertisers[0].label;

  const mixpanelTrackTargetSubmit = () => {
    // Mixpanel block. Dynamically get the app location from location hash for correct Mixpanel track info variety
    const hashString = location && location.hash ? location.hash.split('/')[1] : '';
    const appLocation = startCase(hashString).split(' ').join('-') || 'Audience';
    const props = {
      'Dynamically update segment': isDynamic,
      'Audience Name': audienceName,
      'was amplified': isAmplified && currentAmplificationMode === 'custom',
      'always on amplified': currentAmplificationMode === 'always_on',
      ...(currentAmplificationMode === 'custom' && { advertiser: selectedAdvertiser.label }),
      appName: 'Audience',
      channel: getChannelName(channel),
      ...(currentAmplificationMode === 'custom' && { market: selectedMarket.label }),
      widget: widgetName || hashString,
      ...(isAmplified && {
        'Estimated Reach': getAmplifiedEstimatedReachGoal(bidstreamUsers, amplifiedReachGoal.value),
      }),
      ...(isAmplified && { 'segment type': audienceSegment.label }),
      ...(isAmplified && { 'scale number': amplifiedReachGoal.label }),
      ...(isAmplified && { 'deterministic activation': isAudienceDeterministicActivated }),
      ...(currentAmplificationMode === 'always_on' && { data_contract_id: dataContractId }),
      ...(currentAmplificationMode === 'always_on' && { category_id: categoryId }),
    };
    MixpanelCommon.sendToMixpanel(`${appLocation} - Activated Audience Settings`, props);
  };

  const permissionsService = getAbiPermissionsService();

  return (
    <div className="audience-target-modal-component">
      <ModalWithConfirmationButtons
        width="557px"
        modalTitle={modalTitle}
        isOpen={isOpen || isProcessing}
        isCancelDisabled={isProcessing}
        isSubmitDisabled={
          !!errorMsgCustomPriceDeterministic ||
          !!errorMsgCustomPriceAmplified ||
          isProcessing ||
          (currentAmplificationMode === 'always_on' &&
            (isAudienceAlwaysOnActivated || !dataContractText || !categoryExists)) ||
          (currentAmplificationMode === 'custom' &&
            (!(isDeterministic || isAmplified) ||
              (isAudienceAmplifiedActivated && (isAudienceDeterministicActivated || channel === 'articles')) ||
              isEmpty(selectedMarket) ||
              isEmpty(selectedAdvertiser) ||
              isMarketsPromiseLoading))
        }
        maxHeightBeforeScroll="550px"
        submitLabel={'Activate'}
        onSubmit={() => {
          mixpanelTrackTargetSubmit();
          !errorMsgCustomPriceAmplified &&
            !errorMsgCustomPriceDeterministic &&
            currentAmplificationMode === 'custom' &&
            onSubmitCustom(
              selectedMarket,
              selectedAdvertiser,
              isDeterministic,
              isAmplified,
              amplifiedReachGoal,
              isDynamic,
              tremorSyndicateAmplified,
              tremorSyndicateDeterministic,
              isCustomPriceAmplified ? customPriceAmplified : undefined,
              isCustomPriceDeterministic ? customPriceDeterministic : undefined,
              selectedOptimization,
              expirationDate,
              isCustomPriceDeterministic
            );
          currentAmplificationMode === 'always_on' &&
            onSubmitAlwaysOn(parseInt(dataContractId), dataContractText, parseInt(categoryId), alwaysOnReachGoal);
        }}
        onCancel={() => onCancel()}
        showSpinner={isProcessing}
        submitButtonTooltip={'Once you activate your audience, it cannot be edited.'}
        titleMargin={'0 0 16px 0'}
        isLightTheme={true}
        isDisabledEnterKeyPress={
          isProcessing ||
          isEmpty(selectedMarket) ||
          isEmpty(selectedAdvertiser) ||
          !(isDeterministic || isAmplified) ||
          isMarketsPromiseLoading
        }
      >
        <div className="audience-target-modal-content">
          <div className="audience-name-label">
            <span className="label">Audience Name:</span>
            <span className="audience-name-text">{audienceName}</span>
          </div>

          {hasAmplificationModeSelector && (
            <div className="amplification-selector">
              <TabSelector
                tabs={amplificationModes}
                selectedTab={selectedAmplificationModeTab}
                onSelect={onSelectAmplificationMode}
                isDarkTheme={false}
                tabLocation={'start'}
              ></TabSelector>
            </div>
          )}

          {currentAmplificationMode === 'custom' && <div className="label">Activate to market:</div>}
          {currentAmplificationMode === 'custom' && (
            <div className="market-dropdown">
              <Dropdown
                promise={marketsPromise}
                selectedValue={selectedMarket}
                isMulti={false}
                isSearchable={true}
                onSelect={onMarketChange}
                shouldCloseOnSelect={true}
                shouldCloseOnClickOutside={true}
                summaryTextBuilder={marketsSummaryTextBuilder}
                isDisabled={isCustomAudienceActivated || isMarketsDropdownDisabled}
                isLightTheme={true}
              />
            </div>
          )}
          {currentAmplificationMode === 'custom' && (
            <div className="advertiser-dropdown">
              <Dropdown
                promise={advertisersPromise}
                selectedValue={selectedAdvertiser}
                isMulti={false}
                isSearchable={true}
                onSelect={onAdvertiserChange}
                shouldCloseOnSelect={true}
                shouldCloseOnClickOutside={true}
                summaryTextBuilder={advertisersSummaryTextBuilder}
                isDisabled={isCustomAudienceActivated || isEmptyAdvertisers}
                isLightTheme={true}
              />
            </div>
          )}
          {currentAmplificationMode === 'always_on' && (
            <div className="data-contract-input">
              <div className="dropdown-label">Data Contract ID:</div>
              <TextBox
                value={dataContractDisp}
                placeholder="Add Data Contract ID"
                isDarkTheme={false}
                shouldDisplayErrorMessages={true}
                errorMessage={dataContractIdErrorMessage}
                onChange={onDataContractChange}
                onFocus={onDataContractFocus}
                onBlur={onDataContractBlur}
                isDisabled={isAudienceAlwaysOnActivated}
              ></TextBox>
            </div>
          )}
          {currentAmplificationMode === 'always_on' && (
            <div className="category-input">
              <div className="dropdown-label">Category ID:</div>
              <TextBox
                value={categoryId}
                placeholder="Add Category ID"
                isDarkTheme={false}
                shouldDisplayErrorMessages={true}
                errorMessage={categoryIdErrorMessage}
                onChange={onCategoryChange}
                onBlur={onCategoryBlur}
                isDisabled={isAudienceAlwaysOnActivated}
              ></TextBox>
            </div>
          )}
          {channel !== 'articles' && currentAmplificationMode === 'custom' && (
            <div className="checkbox-row">
              <Checkbox
                key={isAmplified}
                label={'Amplify your audience with a lookalike model'}
                onToggle={(e) => onIsAmplifiedClicked(e)}
                isSelected={isAmplified}
                isDisabled={isAudienceAmplifiedActivated || isDisableAmplify}
              ></Checkbox>
              {isAudienceAmplifiedActivated && (
                <ClickableIcon
                  className={'activation-disabled-info'}
                  iconId={'info'}
                  tooltip={'This audience has been amplified and therefore cannot be amplified again.'}
                />
              )}
            </div>
          )}
          {isAmplified && (
            <div className="amplified-part">
              <div className="justify-content-space-between-row">
                <div className="checkbox-cell audience-size-header">
                  <div className="label">Audience Size</div>
                  <ClickableIcon
                    className={'activation-disabled-info amplified-help-description'}
                    iconId={'info'}
                    tooltip={amplifiedHelpDescription}
                  />
                  {((isAudienceAmplifiedActivated && currentAmplificationMode == 'custom') ||
                    (isAudienceAlwaysOnActivated && currentAmplificationMode == 'always_on')) && (
                    <ClickableIcon
                      className={'activation-disabled-info'}
                      iconId={'info'}
                      tooltip={'This audience has been amplified and therefore cannot be amplified again.'}
                    />
                  )}
                </div>
                {permissionsService?.hasPermission(PERMISSION_CUSTOM_AMPLIFY_PERCENTAGE) && (
                  <div className="right-cell-input percentage-input">
                    {currentAmplificationMode === 'custom' && (
                      <TextBox
                        type={'number'}
                        min={MIN_REACH_GOAL}
                        max={MAX_REACH_GOAL}
                        step={REACH_GOAL_STEPS}
                        value={amplifiedReachGoal.value}
                        placeholder="Add Audience Size"
                        isDarkTheme={false}
                        isDisabled={isAudienceAmplifiedActivated || isSegmentOptimized}
                        onChange={onAmplifiedReachGoalChanged}
                        onEnter={onAmplifiedReachGoalChanged}
                        height={customFieldHeight}
                      />
                    )}
                  </div>
                )}
              </div>
              <div className="reach-goal-wrapper">
                {currentAmplificationMode === 'custom' && (
                  <Slider
                    values={reachGoalValues}
                    selectedValue={amplifiedReachGoal}
                    onChange={onAmplifiedReachGoalChanged}
                    isDisabled={isAudienceAmplifiedActivated || isSegmentOptimized}
                  />
                )}
                {currentAmplificationMode === 'always_on' && (
                  <Slider
                    values={reachGoalValues}
                    selectedValue={alwaysOnReachGoal}
                    onChange={onAlwaysOnReachGoalChanged}
                    isDisabled={isAudienceAlwaysOnActivated || isSegmentOptimized}
                  />
                )}
              </div>
              {allowOptimizeSegment && (
                <div className="checkbox-row">
                  <Switch
                    checked={isSegmentOptimized}
                    onChange={(e) => {
                      setIsSegmentOptimized(e.target.checked);
                      setSelectedOptimization(
                        e.target.checked ? optimizationOptions[1].value : optimizationOptions[0].value
                      );
                      setAmplifiedReachGoal(maxReachGoal);
                    }}
                  />
                  <span>Segment Optimization</span>
                  <RadioGroup
                    disabled
                    inline
                    onChange={(e) => setSelectedOptimization(e.currentTarget.value)}
                    selectedValue={selectedOptimization}
                  >
                    <Radio disabled={!isSegmentOptimized} value={optimizationOptions[1].value}>
                      {optimizationOptions[1].label}
                    </Radio>
                    <Radio disabled={!isSegmentOptimized} value={optimizationOptions[2].value}>
                      {optimizationOptions[2].label}
                    </Radio>
                  </RadioGroup>
                </div>
              )}
              <div className="justify-content-space-between-row">
                {currentAmplificationMode === 'custom' && allowCustomPriceSegment && (
                  <>
                    <Checkbox
                      key={isCustomPriceAmplified}
                      label={CUSTOM_PRICE}
                      onToggle={setIsCustomPriceAmplified}
                      isSelected={isCustomPriceAmplified}
                    ></Checkbox>
                    {isCustomPriceAmplified && (
                      <div className="right-cell-input">
                        <TextBox
                          type={'number'}
                          min={MIN_CUSTOM_PRICE}
                          max={MAX_CUSTOM_PRICE}
                          step={CUSTOM_PRICE_STEPS}
                          shouldDisplayErrorMessages={true}
                          errorMessage={errorMsgCustomPriceAmplified}
                          value={customPriceAmplified}
                          placeholder={ADD_CUSTOM_PRICE}
                          isDarkTheme={false}
                          onChange={setCustomPriceAmplified}
                          onEnter={setCustomPriceAmplified}
                          height={customFieldHeight}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="row-label">Syndicate To:</div>
              <div className="madie-provider-dropdown">
                {currentAmplificationMode === 'custom' && (
                  <Dropdown
                    isMulti
                    values={SYNDICATION_VALUES}
                    selectedValues={tremorSyndicateAmplified}
                    summaryTextBuilder={getSummaryTextBuilder('All media providers')}
                    onSelect={setTremorSyndicateAmplified}
                  />
                )}
              </div>
              {currentAmplificationMode === 'custom' && (
                <EstimatedReachWidget
                  getReachNumber={() =>
                    Promise.all([
                      marketsAndAdvertisersPromise,
                      getAmplifiedEstimatedReachGoal(bidstreamUsers, amplifiedReachGoal.value),
                    ]).then((values) => values[1])
                  }
                  params={[amplifiedReachGoal.value]}
                  reachType={'users'}
                />
              )}
              {currentAmplificationMode === 'always_on' && (
                <EstimatedReachWidget
                  getReachNumber={() =>
                    Promise.all([
                      marketsAndAdvertisersPromise,
                      getAmplifiedEstimatedReachGoal(bidstreamUsers, alwaysOnReachGoal.value),
                    ]).then((values) => values[1])
                  }
                  params={[alwaysOnReachGoal.value]}
                  reachType={'users'}
                />
              )}
              {channel !== 'articles' && currentAmplificationMode === 'custom' && (
                <Separator className="content-separator" />
              )}
            </div>
          )}
          {channel !== 'articles' && currentAmplificationMode === 'custom' && allowDeterministicSegment && (
            <div className="checkbox-row">
              <Checkbox
                key={isDeterministic}
                label={'Create a deterministic segment based on your defined audience'}
                onToggle={onIsDeterministicClicked}
                isSelected={isDeterministic}
                isDisabled={isAudienceDeterministicActivated}
              ></Checkbox>
              {isAudienceDeterministicActivated && (
                <ClickableIcon
                  className={'activation-disabled-info'}
                  iconId={'info'}
                  tooltip={'This audience has been targeted and therefore cannot be targeted again.'}
                />
              )}
            </div>
          )}
          {channel !== 'articles' && currentAmplificationMode === 'custom' && isDeterministic && (
            <div className="deterministic-part">
              {isDeterministicDynamicAllowed && (
                <div className="checkbox-row">
                  <Checkbox
                    key={isDynamic}
                    label={'Dynamically update segment'}
                    onToggle={onIsDynamicClicked}
                    isSelected={isDynamic}
                  ></Checkbox>
                </div>
              )}
              {!isDynamic && (
                <div className="checkbox-row">
                  Expiration date:
                  <DatepickerButton
                    initialDate={convertDateToSeconds(expirationDate)}
                    month={expirationDate}
                    minDate={minDate}
                    maxDate={maxDate}
                    onDaySelected={(dateObj) => onDaySelected(dateObj)}
                  />
                </div>
              )}
              {allowCustomPriceSegment && (
                <div className="justify-content-space-between-row">
                  <Checkbox
                    key={isCustomPriceDeterministic}
                    label={CUSTOM_PRICE}
                    onToggle={setIsCustomPriceDeterministic}
                    isSelected={isCustomPriceDeterministic}
                  ></Checkbox>
                  {isCustomPriceDeterministic && (
                    <div className="right-cell-input">
                      <TextBox
                        type={'number'}
                        min={MIN_CUSTOM_PRICE}
                        max={MAX_CUSTOM_PRICE}
                        step={CUSTOM_PRICE_STEPS}
                        shouldDisplayErrorMessages={true}
                        errorMessage={errorMsgCustomPriceDeterministic}
                        value={customPriceDeterministic}
                        placeholder={ADD_CUSTOM_PRICE}
                        isDarkTheme={false}
                        onChange={setCustomPriceDeterministic}
                        onEnter={setCustomPriceDeterministic}
                        height={customFieldHeight}
                      />
                    </div>
                  )}
                </div>
              )}
              <div className="row-label">Syndicate To:</div>
              <div className="madie-provider-dropdown">
                <Dropdown
                  isMulti
                  values={SYNDICATION_VALUES}
                  selectedValues={tremorSyndicateDeterministic}
                  summaryTextBuilder={getSummaryTextBuilder('All media providers')}
                  onSelect={setTremorSyndicateDeterministic}
                />
              </div>
              <EstimatedReachWidget
                getReachNumber={() => getSegmentIds(audienceSegment, channel)}
                params={[audienceSegment, channel]}
                reachType={'households'}
              />
            </div>
          )}
        </div>
      </ModalWithConfirmationButtons>
    </div>
  );
};

AudienceTargetModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onSubmitCustom: PropTypes.func.isRequired,
  onSubmitAlwaysOn: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  dspService: PropTypes.object.isRequired,
  marketsAndAdvertisersPromise: PropTypes.object.isRequired,
  audienceName: PropTypes.string.isRequired,
  isProcessing: PropTypes.bool,
  getSegmentIds: PropTypes.func.isRequired,
  audienceSegment: PropTypes.array.isRequired,
  channel: PropTypes.string.isRequired,
  isAudienceDeterministicActivated: PropTypes.bool,
  isAudienceAmplifiedActivated: PropTypes.bool,
  isAudienceAlwaysOnActivated: PropTypes.bool,
  activatedAmplifiedThreshold: PropTypes.number,
  activatedAlwaysOnThreshold: PropTypes.number,
  activatedMarket: PropTypes.object,
  activatedAdvertiser: PropTypes.object,
  activatedDataContractId: PropTypes.number,
  activatedDataContractText: PropTypes.string,
  activatedCategoryId: PropTypes.number,
  getAmplifiedEstimatedReachGoal: PropTypes.func.isRequired,
  widgetName: PropTypes.string,
  hasAmplificationModeSelector: PropTypes.bool,
  getDataContract: PropTypes.func,
  getTaxonomyCategory: PropTypes.func,
  isDeterministicDynamicAllowed: PropTypes.bool,
  allowDeterministicSegment: PropTypes.bool,
  allowCustomPriceSegment: PropTypes.bool,
  allowOptimizeSegment: PropTypes.bool,
};

AudienceTargetModal.defaultProps = {
  isDeterministicDynamicAllowed: true,
};

export default AudienceTargetModal;
